import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useEffect, useRef, useState } from 'react';
import ModalCentered from '../../../components/ModalCentered';
import { registerBid } from '../../../api/Commerce';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import Spinner from 'react-bootstrap/esm/Spinner';

function NewBidModal({
  showNewBidModal,
  setShowNewBidModal,
  vehicles,
  competitionId,
  showBidSuccessToast,
}) {
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [openForBids, setOpenForBids] = useState(false);
  const [addBidError, setAddBidError] = useState(false);
  const [addBidErrorMsg, setAddBidErrorMsg] = useState('');
  const [updating, setUpdating] = useState(false);
  const formikRef = useRef();

  useEffect(() => {
    setFilteredVehicles([...vehicles]);

    if (vehicles.filter((v) => v.bidStatus === 'openforbids').length === 0) {
      setOpenForBids(false);
    } else {
      setOpenForBids(true);
    }
  }, [vehicles]);

  const filterVehicles = (value) => {
    if (value.length > 0) {
      const newFiltered = filteredVehicles.filter((vehicle) =>
        vehicle.racingNumbers.some((rn) =>
          rn.toString().includes(value.toString())
        )
      );
      setFilteredVehicles(newFiltered);
    } else {
      setFilteredVehicles(vehicles);
    }
  };

  const createBidSchema = Yup.object().shape({
    name: Yup.string().required('Ange namn'),
    city: Yup.string()
      .matches(/^[A-Öa-ö\- ]*$/, 'Endast bokstäver tillåtet')
      .min(2, 'För kort postort')
      .required('Du måste ange en postort'),
    vehicleId: Yup.string().required('Du måste välja ett fordon'),
    phoneNumber: Yup.string()
      .matches(/^[0-9\-]+$/, 'Endast siffror tillåtet')
      .required('Ange telefonnummer'),
  });

  const confirmAddBtn = (
    <>
      {updating ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          <span className="visually-hidden">Loading...</span>
          Lägg anbud
        </>
      ) : openForBids ? (
        'Lägg anbud'
      ) : (
        'OK'
      )}
    </>
  );

  const newBidModalBody = (
    <>
      {openForBids ? (
        <div>
          <p className="text-secondary">
            Välj fordon, ange antal anbud samt fyll i anbudsläggares uppgifter
            för att lägga till anbud.
          </p>

          <Formik
            initialValues={{
              competitionId: competitionId,
              vehicleId: '',
              totalBids: 1,
              name: '',
              city: '',
              phoneNumber: '',
            }}
            onSubmit={async (values) => {
              setUpdating(true);
              const result = await registerBid(values);
              if (result) {
                switch (result.statusCode) {
                  case 200:
                    setAddBidError(false);
                    setShowNewBidModal(false);
                    showBidSuccessToast(true);
                    break;
                  default:
                    setAddBidError(true);
                    setAddBidErrorMsg(result.message);
                    break;
                }
                setUpdating(false);
              }
            }}
            innerRef={formikRef}
            validationSchema={createBidSchema}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              isValid,
              errors,
              validateForm,
            }) => (
              <FormikForm
                noValidate
                id="form-create-bid"
                onSubmit={handleSubmit}
                className="d-flex flex-column gap-3"
              >
                <Form.Group controlId="vehicle">
                  <Form.Label>Fordon</Form.Label>
                  <Form.Control
                    onChange={(e) => filterVehicles(e.target.value)}
                    placeholder="Sök"
                  />
                </Form.Group>

                <div
                  className="d-flex flex-wrap gap-2"
                  style={{ maxHeight: '200px', overflowY: 'auto' }}
                >
                  {filteredVehicles.map((vehicle) => {
                    return (
                      <div key={vehicle.vehicleId}>
                        {vehicle.bidStatus === 'openforbids' && (
                          <Button
                            name="vehicleId"
                            disabled={values.vehicleId === vehicle.vehicleId}
                            onClick={(e) => {
                              handleChange(e);
                            }}
                            value={vehicle.vehicleId}
                            variant={
                              values.vehicleId === vehicle.vehicleId
                                ? 'primary'
                                : 'outline-primary'
                            }
                          >
                            {vehicle.racingNumbers.map((rn, index) => {
                              let racingNumber = rn;
                              if (index < vehicle.racingNumbers.length - 1) {
                                racingNumber += ', ';
                              }

                              return racingNumber;
                            })}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Form.Control
                  type="hidden"
                  name="vehicleId"
                  value={values.vehicleId}
                  isInvalid={!!errors.vehicleId}
                />

                <Form.Control.Feedback type="invalid">
                  {errors.vehicleId}
                </Form.Control.Feedback>

                <hr />
                <Form.Group controlId="name">
                  <Form.Label>Namn anbudsläggare</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="Ange anbudsläggare"
                    value={values.name}
                    onChange={(e) => handleChange(e)}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="city">
                  <Form.Label>Stad</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ange stad"
                    value={values.city}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    isInvalid={!!errors.city}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.city}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="phoneNumber">
                  <Form.Label>Telefonnummer</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ange telefonnummer"
                    value={values.phoneNumber}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    isInvalid={!!errors.phoneNumber}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phoneNumber}
                  </Form.Control.Feedback>
                </Form.Group>

                {addBidError && (
                  <div className="text-danger">{addBidErrorMsg}</div>
                )}
              </FormikForm>
            )}
          </Formik>
        </div>
      ) : (
        <div>Det behöver finnas öppna fordon för att lägga till anbud.</div>
      )}
    </>
  );

  return (
    <ModalCentered
      header="Nytt anbud"
      show={showNewBidModal}
      confirmBtnText={confirmAddBtn}
      body={newBidModalBody}
      onHide={() => {
        setShowNewBidModal(false);
        setAddBidError(false);
        setAddBidErrorMsg('');
      }}
      onConfirm={async () => {
        if (openForBids) {
          formikRef.current.handleSubmit();
        } else {
          setShowNewBidModal(false);
        }
      }}
    />
  );
}

export default NewBidModal;
